import type { FunctionComponent } from 'react'
import { Fragment } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import styled from 'styled-components'
import { toDataSearch } from '../../../links'
import toSearchParams from '../../utils/toSearchParams'
import { activeFiltersParam, queryParam } from '../../../pages/SearchPage/query-params'
import formatCount from '../../utils/formatCount'
import type { DataIconType } from './DataIcon'
import DataIcon from './DataIcon'
import { getSearchResultPath } from '../../utils/searchUtils'
import type { DataResult } from '../../../pages/SearchPage/types'
import Paragraph from '../Paragraph/Paragraph'
import Link, { Variant } from '../Links/Link/Link'
import Heading from '../Heading/Heading'
import Icon from '../Icon/Icon'

const StyledLink = styled(Link)`
  color: var(--dark);
  &:hover {
    color: var(--red);
  }
`

const StyledCard = styled.div`
  display: flex;
  flex-direction: row;
  border: var(--greyLight) 1px solid;
  justify-content: flex-start;
  width: inherit;
  padding: 0.5rem;
`

const StyledCardContent = styled.div`
  padding: 0;
  min-height: inherit;
  width: calc(100% - 4.75rem);
`

const StyledCardMedia = styled.div`
  position: relative;
  overflow: hidden;
  line-height: 0;
  display: flex;
  width: 100%;
  flex: 1 1 auto;
  align-self: stretch;
  max-width: 4.75rem;
`

const StyledParagraph = styled(Paragraph)`
  white-space: nowrap;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  margin: 0;
`

const StyledParagraphLink = styled(Link)`
  font-weight: normal;
  color: inherit;
`

const StyledIcon = styled(Icon)`
  background: var(--greyUltraLight);
  width: 3.75rem;
  height: 3.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ParagraphWrapper = styled.div`
  flex-wrap: nowrap;
  max-width: 100%;
`

interface DataCardProps {
  type: DataIconType
  label: string
  count: number
  results: DataResult[]
  query: string
}

const DataCard: FunctionComponent<DataCardProps> = ({
  type,
  label,
  count,
  results,
  query,
  ...otherProps
}) => {
  return (
    <StyledCard key={type} {...otherProps}>
      <StyledCardMedia>
        <StyledIcon size={60}>
          <DataIcon type={type} />
        </StyledIcon>
      </StyledCardMedia>
      <StyledCardContent>
        <div>
          <Heading type="h4">
            <StyledLink
              variant={Variant.Basic}
              forwardedAs={RouterLink}
              to={{
                ...toDataSearch(),
                search: toSearchParams([
                  [queryParam, query],
                  [activeFiltersParam, [{ type: 'dataTypes', values: [type] }]],
                ]).toString(),
              }}
            >
              {`${label} (${formatCount(count)})`}
            </StyledLink>
          </Heading>
        </div>

        <ParagraphWrapper>
          <StyledParagraph small>
            {results?.map((location, index) => {
              return (
                <Fragment key={location?.id}>
                  <StyledParagraphLink to={getSearchResultPath(location)} forwardedAs={RouterLink}>
                    {location?.label}
                  </StyledParagraphLink>
                  {index !== results.length - 1 ? `, ` : ''}
                </Fragment>
              )
            })}
          </StyledParagraph>
        </ParagraphWrapper>
      </StyledCardContent>
    </StyledCard>
  )
}

export default DataCard
